import { BellAlertIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Routes, useParams, useNavigate } from 'react-router-dom';

import {
	classNames,
	getUserFullName,
} from '../../components/helpers/functions';
import Badge from '../../components/Badge';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import ProtectedRoute from '../../components/helpers/ProtectedRoute';
import DataTable from '../../components/DataTable';

import useAsync from '../../hooks/useAsync';
import useModal from '../../hooks/useModal';
import useSearchParams from '../../hooks/useSearchParams';

import { getServices } from '../../api/services';
import { getModuleCategories } from '../../api/modules';

import ServiceForm from './ServiceForm';
import usePrevious from '../../hooks/usePrevious';
import { AvatarGenerated } from '../../components/Avatar';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';

const SERVICE_MODULE_ID = process.env.REACT_APP_MODULE_ID_SERVICE;
const EVENT_MODULE_ID = process.env.REACT_APP_MODULE_ID_EVENT;

export const TasksEntry = () => {
	const navigate = useNavigate();

	const { id, tab } = useParams();
	const [currentTab, setCurrentTab] = useState(null);

	const { open: isAddNewTaskModalOpen, setOpen: openAddNewTaskModal } =
		useModal('taskAdd');

	const { open: isServiceOverviewClosed, setOpen: openServiceOverview } =
		useModal('serviceOverview');

	const {
		page: pageFilter,
		category: categoryFilter,
		sortBy: sortByFilter,
		sortOrder: sortOrderFilter,
	} = useSearchParams();

	const {
		value: services,
		execute: refreshServices,
		status: isLoadingTasks,
	} = useAsync(getServices, {
		defaultPayload: { categoryId: categoryFilter },
	});

	const { value: categories, execute: onRefreshCategories } = useAsync(
		getModuleCategories,
		{
			defaultPayload: { id: EVENT_MODULE_ID },
			silent: true,
		},
	);

	useEffect(() => {
		if (id) {
			openServiceOverview({
				id,
				onClose: () => {
					refreshServices();
				},
				path: '/manage/tasks',
			});
		}
	}, [id]);

	useEffect(() => {
		refreshServices(
			{
				page: pageFilter,
				categoryId: categoryFilter,
				sortBy: sortByFilter,
				sortOrder: sortOrderFilter,
			},
			null,
			{
				silent: true,
			},
		);

		setCurrentTab(categoryFilter);
	}, [pageFilter, categoryFilter, sortByFilter, sortOrderFilter]);

	const prevAddNewTaskModalOpen = usePrevious(isAddNewTaskModalOpen);
	useEffect(() => {
		if (!isAddNewTaskModalOpen && prevAddNewTaskModalOpen) {
			refreshServices(null, null, {
				silent: true,
			});
		}
	}, [isAddNewTaskModalOpen]);

	const handleTabChange = (tab) => {
		const search = new URLSearchParams();

		if (tab) {
			search.append('category', tab);
			navigate({ search: search ? search.toString() : '' });
		} else {
			navigate({ search: '' });
		}

		//setCurrentTab(tab);
	};

	return (
		<MainContent maxWidth="max-w-7xl">
			<PageHeading
				actions={[
					{
						name: 'Add New Task',
						onClick: () => openAddNewTaskModal(),
					},
				]}
				divider={false}
			>
				Manage Tasks
			</PageHeading>
			<div className="grid grid-cols-12">
				<div className="col-span-2">
					<nav
						className={classNames('flex flex-1 flex-col mt-4')}
						aria-label="Sidebar"
					>
						<ul role="list" className="-mx-2 space-y-1">
							{[
								{
									name: 'All',
									onClick: () => {
										handleTabChange(null);
										//navigate({ search: '' });
									},
									current: currentTab === 'all' || !currentTab,
									//count: documents?.absoluteTotal,
								},

								...(categories
									? categories?.map((category) => ({
											name: category?.name,
											onClick: () => {
												handleTabChange(category?.id);
											},
											current: currentTab === category?.id,
										}))
									: []),

								{
									name: 'Uncategorized',
									onClick: () => {
										handleTabChange('uncategorized');
									},
									current: currentTab === 'uncategorized',
									//count: documentBreakdown?.uncategorized?.length,
								},
							].map((item) => {
								return (
									<li key={item.name}>
										<div
											onClick={() => item.onClick()}
											className={classNames(
												item.current
													? 'bg-gray-50 text-indigo-600'
													: 'text-gray-700 hover:text-indigo-600 hover:bg-gray-50',
												'group flex gap-x-3 rounded-md p-2 text-xs leading-6 font-medium cursor-pointer',
											)}
										>
											{item.icon && (
												<item.icon
													className={classNames(
														item.current
															? 'text-indigo-600'
															: 'text-gray-400 group-hover:text-indigo-600',
														'h-6 w-6 shrink-0',
													)}
													aria-hidden="true"
												/>
											)}
											{item.name}
											{typeof item.count !== 'undefined' ? (
												<span
													className="ml-auto w-9 min-w-max whitespace-nowrap rounded-full bg-white px-2.5 py-0.5 text-center text-xxs font-medium leading-5 text-gray-600 ring-1 ring-inset ring-gray-200"
													aria-hidden="true"
												>
													{item.count || 0}
												</span>
											) : null}
										</div>
									</li>
								);
							})}
						</ul>
					</nav>
				</div>
				<div className="col-span-10 mx-2 px-4 mt-4">
					{isLoadingTasks === 'pending' ? (
						<LoadingSkeleton />
					) : (
						<DataTable
							data={{
								rows: services?.rows,
							}}
							manualSorting={false}
							columns={[
								{
									header: 'Name',
									accessorKey: 'name',
									cell: (info) => info?.getValue() || 'N/A 3',
								},
								{
									header: 'Status',
									accessorKey: 'status',
									cell: (info) => {
										const task = info?.row?.original;

										if (task?.completed) {
											return (
												<Badge isBeta size="xxs" status="success">
													Completed
												</Badge>
											);
										} else if (task?.closed) {
											return (
												<Badge isBeta size="xxs">
													Closed
												</Badge>
											);
										} else {
											const nextEvent = info?.row?.original?.nextEvent;

											const date =
												nextEvent?.status === 'ESTIMATED'
													? nextEvent?.estimatedDate
													: nextEvent?.scheduledDate;

											const isOverdue =
												nextEvent?.status === 'ESTIMATED'
													? moment().isAfter(moment(date).startOf('d'))
													: moment().isAfter(moment(date).endOf('d'));

											if (nextEvent) {
												if (nextEvent.status === 'SCHEDULED') {
													return (
														<Badge
															isBeta
															size="xxs"
															status={isOverdue ? 'error' : 'default'}
														>
															{isOverdue ? 'Overdue: Scheduled' : `Scheduled`}
														</Badge>
													);
												} else {
													return (
														<Badge
															isBeta
															size="xxs"
															status={isOverdue ? 'error' : 'default'}
														>
															{isOverdue ? 'Overdue: Estimated' : `Estimated`}
														</Badge>
													);
												}
											}

											return (
												<Badge isBeta size="xxs">
													To do
												</Badge>
											);
										}

										return '';

										const nextEvent = info?.row?.original?.nextEvent;

										const date =
											nextEvent?.status === 'ESTIMATED'
												? nextEvent?.estimatedDate
												: nextEvent?.scheduledDate;

										if (!nextEvent || !date) {
											return (
												<Badge isBeta size="xxs" status="success">
													Completed
												</Badge>
											);
										}

										const isOverdue =
											nextEvent?.status === 'ESTIMATED'
												? moment().isAfter(moment(date).startOf('d'))
												: moment().isAfter(moment(date).endOf('d'));

										if (isOverdue) {
											if (
												nextEvent?.status === 'ESTIMATED' ||
												nextEvent?.status === 'SCHEDULED'
											) {
												const title = `Overdue`;
												return (
													<Badge isBeta size="xxs" status="error">
														{title}
													</Badge>
												);
											}
										} else {
											if (nextEvent?.status === 'ESTIMATED') {
												const title = `Estimated`;
												return (
													<Badge isBeta size="xxs" status="default">
														{title}
													</Badge>
												);
											} else if (nextEvent?.status === 'SCHEDULED') {
												const title = `Scheduled`;
												return (
													<Badge isBeta size="xxs" status="success">
														{title}
													</Badge>
												);
											}
										}

										return <div></div>;
									},
								},
								{
									header: 'Due',
									accessorKey: 'nextEvent',
									sortingFn: (a, b) => {
										const aEvent = a?.original?.nextEvent;
										const bEvent = b?.original?.nextEvent;
										const aDate =
											aEvent?.status === 'ESTIMATED'
												? aEvent?.estimatedDate
												: aEvent?.scheduledDate;
										const bDate =
											bEvent?.status === 'ESTIMATED'
												? bEvent?.estimatedDate
												: bEvent?.scheduledDate;

										return moment.utc(aDate).diff(moment.utc(bDate));
									},
									cell: (info) => {
										const nextEvent = info?.getValue();

										const date =
											nextEvent?.status === 'ESTIMATED'
												? nextEvent?.estimatedDate
												: nextEvent?.scheduledDate;

										return date
											? moment(date).isBefore(moment()) ||
												moment(date).isBetween(
													moment(),
													moment().add(7, 'days'),
												)
												? moment(date).fromNow()
												: moment(date).format('MM/DD/YYYY')
											: '';
									},
								},

								{
									header: 'Assigned To',
									accessorKey: 'assignedToUser',
									cell: (info) => {
										const user = info?.getValue();
										return user ? (
											<div className="flex items-center">
												<AvatarGenerated
													id={getUserFullName(user)}
													size="xxs"
													className={'mr-2'}
												/>{' '}
												{getUserFullName(user)}
											</div>
										) : (
											''
										);
									},
								},

								{
									header: 'Event Type',
									accessorKey: 'category.name',
									cell: (info) => {
										const eventCategoryName =
											info?.row?.original?.defaultEventCategory?.name;

										return eventCategoryName || '';
									},
								},
								{
									header: 'Last Action',
									accessorKey: 'lastEvent',
									sortingFn: (a, b) => {
										return moment.utc(a).diff(moment.utc(b));
									},
									cell: (info) => {
										const lastEvent = info?.getValue();

										if (lastEvent) {
											return `${moment(lastEvent?.completedDate).format(
												'MM/DD/YYYY',
											)}`;
										}

										/*const date =
										nextEvent?.status === 'ESTIMATED'
											? nextEvent?.estimatedDate
											: nextEvent?.scheduledDate;*/

										return <div></div>;
									},
								},
							]}
							onRow={(row) => {
								navigate(`/manage/tasks/${row.original?.id}`);
							}}
							hasCheckboxes={false}
							usePagination={false}
							useExport={false}
							disableTitle={true}
							initialState={{
								sortBy: [
									{
										id: 'nextEvent',
										desc: false,
									},
								],
							}}
							useSearch={false}
						/>
					)}
				</div>
			</div>
		</MainContent>
	);
};

export default () => (
	<Routes>
		<ProtectedRoute exact path={`/manage/tasks/new`} component={ServiceForm} />
		<ProtectedRoute
			exact
			path={`/manage/tasks/:id/edit`}
			component={ServiceForm}
		/>
		<ProtectedRoute exact path={`/manage/tasks`} component={Tasks} />
		<ProtectedRoute exact path={`/manage/tasks/:id`} component={Tasks} />
	</Routes>
);
